/**
 * Map tile source definitions.
 * Two basemaps:
 *   street   — OSM raster tiles (standard OSM tile server, no API key)
 *   satellite — ArcGIS raster imagery + OpenFreeMap symbol layers (labels, POIs)
 *              This is the former "satellite_labels"/"Hybride" entry.
 *              The raw imagery-only basemap was removed — no practical value standalone.
 */

import type { StyleSpecification } from 'maplibre-gl';

const ARCGIS_TILES = [
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
];

export const MAP_STYLES = {
  street: {
    label: 'Carte',
    icon: 'Map' as const,
    styleUrl: {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['/tiles/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxzoom: 19,
        },
      },
      layers: [
        {
          id: 'osm-base',
          type: 'raster',
          source: 'osm',
        },
      ],
    } as StyleSpecification,
  },
  satellite: {
    label: 'Satellite',
    icon: 'Satellite' as const,
    // ArcGIS raster + OpenFreeMap symbol layers only (labels, POIs).
    // Built dynamically by fetching OpenFreeMap style and keeping only symbol layers.
    // ⚠️ ArcGIS uses {z}/{y}/{x} — y and x are SWAPPED vs standard {z}/{x}/{y}
    styleUrl: null as string | StyleSpecification | null,
  },
} as const;

export type MapStyleKey = keyof typeof MAP_STYLES;
export const MAP_STYLE_CYCLE: MapStyleKey[] = ['street', 'satellite'];

const OPENFREEMAP_LIBERTY_URL = 'https://tiles.openfreemap.org/styles/liberty';

/**
 * Builds the satellite basemap style by fetching the OpenFreeMap liberty style,
 * retaining only symbol layers (labels, POIs), and prepending the ArcGIS raster source.
 * Result is cached in Map.tsx to avoid repeated network fetches.
 */
export async function buildHybridStyle(): Promise<StyleSpecification> {
  const response = await fetch(OPENFREEMAP_LIBERTY_URL);
  const ofmStyle: StyleSpecification = await response.json();

  const symbolLayers = ofmStyle.layers
    .filter((l) => l.type === 'symbol')
    .map((layer) => ({
      ...layer,
      paint: {
        ...(layer as { paint?: Record<string, unknown> }).paint,
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1.5,
      },
    }));

  return {
    version: 8,
    glyphs: (ofmStyle as { glyphs?: string }).glyphs ?? '',
    sprite: (ofmStyle as { sprite?: string }).sprite ?? '',
    sources: {
      arcgis: {
        type: 'raster',
        tiles: ARCGIS_TILES,
        tileSize: 256,
        attribution: 'Tiles © Esri',
      },
      ...(ofmStyle.sources as Record<string, never>),
    },
    layers: [
      { id: 'arcgis-base', type: 'raster', source: 'arcgis' },
      ...symbolLayers,
    ],
  };
}
